import React, { useState, useEffect } from 'react';
import { apiGet } from '../api';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { WebhookEventsPanel } from '../components/WebhookEventsPanel';
import { 
  Building2, Users, DollarSign, TrendingUp, Loader2, 
  CreditCard, MessageSquare, BarChart3, RefreshCw, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export const AdminDashboardPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [studios, setStudios] = useState([]);
  const [recentStudios, setRecentStudios] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, studiosRes] = await Promise.all([
        apiGet('/api/admin/stats'),
        apiGet('/api/admin/studios')
      ]);
      setStats(statsRes);
      setStudios(studiosRes);
      // Get 5 most recent studios
      const sorted = [...studiosRes].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      ).slice(0, 5);
      setRecentStudios(sorted);
    } catch (e) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="admin-dashboard-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Platform Overview</h1>
            <p className="text-muted-foreground">Monitor your platform's performance and manage settings</p>
          </div>
          <Button variant="outline" onClick={fetchData} data-testid="admin-dashboard-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList data-testid="admin-dashboard-tabs">
            <TabsTrigger value="overview" data-testid="admin-tab-overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="webhooks" data-testid="admin-tab-webhooks">
              <Zap className="w-4 h-4 mr-2" />
              Webhooks
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Total Studios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{stats?.total_studios || 0}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="w-4 h-4" />
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{stats?.total_users || 0}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{stats?.total_transactions || 0}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Platform Fees
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-emerald-600">€{(stats?.platform_fees_collected || 0).toFixed(2)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/admin/subscriptions">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Subscription Plans
                </CardTitle>
                <CardDescription>
                  Manage platform subscription tiers for studios
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full">Manage Plans</Button>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/admin/settings">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Platform Settings
                </CardTitle>
                <CardDescription>
                  Configure platform-wide settings and integrations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full">View Settings</Button>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Recent Studios */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Studios</CardTitle>
                <CardDescription>Latest studios registered on the platform</CardDescription>
              </div>
              <Link to="/admin/studios">
                <Button variant="outline" size="sm">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {recentStudios.length > 0 ? (
              <div className="space-y-3">
                {recentStudios.map((studio, i) => (
                  <div 
                    key={studio.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                    data-testid={`recent-studio-${i}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        {/* Bug R17 fix: inline name + URL as one visual unit. */}
                        <p className="font-medium leading-tight">
                          {studio.name}
                          <span className="ml-2 text-sm font-normal text-muted-foreground">
                            · studioscheduler.ie/{studio.slug}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant={studio.is_active ? 'default' : 'secondary'}>
                        {studio.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <p className="text-sm text-muted-foreground">
                        {new Date(studio.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Building2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No studios registered yet</p>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="webhooks">
            <WebhookEventsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};
